import { getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'

import { SelectOption } from '../../../components/SelectDropdown/SelectDropdown'

import { User } from '../../../models/User'
import { formatDate } from '../../../common/dateUtils'

export const MAX_DOCUMENT_BATCH_FILES = 10

export const MAX_DOCUMENT_FILE_SIZE_BYTES = 120 * 1024 * 1024
export const MAX_DOCUMENT_FILE_SIZE_BYTES_ZDR = 20 * 1024 * 1024
export const MAX_PASSWORD_PROTECTED_DOCUMENT_FILE_SIZE_BYTES = 20 * 1024 * 1024
export const MAX_CONCURRENT_FILE_READS = 3

export enum DocumentUploadSource {
  ZOCKS = 'zocks',
  INTEGRATION = 'integration',
  EMAIL_ATTACHMENT = 'emailAttachment',
}

export const DOCUMENT_UPLOAD_SOURCE_LABEL: Record<DocumentUploadSource, string> = {
  [DocumentUploadSource.ZOCKS]: 'manual_upload',
  [DocumentUploadSource.INTEGRATION]: 'integration',
  [DocumentUploadSource.EMAIL_ATTACHMENT]: 'email_attachment',
}

const CONTENT_TYPE_BY_EXTENSION: Record<string, string> = {
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  txt: 'text/plain',
  csv: 'text/csv',
}

export const inferContentType = (name: string): string => {
  const extension = name.split('.').pop()?.toLowerCase()
  return (extension && CONTENT_TYPE_BY_EXTENSION[extension]) || 'application/octet-stream'
}

export const zocksUserToSelectOption = (user: User): SelectOption => {
  const displayName = getUserDisplayName(user)
  return {
    id: user.id,
    label: `${displayName} (${user.email})`,
  }
}

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) {
    return '0 B'
  }

  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

// Upsell/explanatory size-limit toasts stay up longer than the default so there's time to read and act on them.
export const LONG_TOAST_DELAY_MS = 10000

export const buildFileTooLargeToast = (accountHasZDRPolicy: boolean, maxFileSizeBytes: number, filePhrase: 'Each file' | 'The file') =>
  accountHasZDRPolicy
    ? {
        variant: 'error' as const,
        title: 'File is over 20 MB',
        messages: ['Zero Data Retention (ZDR) limits uploads to 20 MB. Upload a smaller file, or turn off ZDR in Settings.'],
        delay: LONG_TOAST_DELAY_MS,
      }
    : {
        variant: 'error' as const,
        title: 'Invalid file size',
        messages: [`${filePhrase} must not be empty and no larger than ${formatFileSize(maxFileSizeBytes)}.`],
      }

const chunk = <T>(items: T[], size: number): T[][] => Array.from({ length: Math.ceil(items.length / size) }, (_, i) => items.slice(i * size, i * size + size))

// Runs `task` over `items`, at most `limit` in flight at a time, preserving input order in the returned results.
export const runWithConcurrencyLimit = async <T, R>(items: T[], limit: number, task: (item: T) => Promise<R>): Promise<R[]> => {
  const results: R[] = []

  await chunk(items, limit).reduce(async (previousChunks, currentChunk) => {
    await previousChunks
    results.push(...(await Promise.all(currentChunk.map(task))))
  }, Promise.resolve())

  return results
}

export const hashFile = async (file: File) => {
  const buffer = await file.arrayBuffer()
  const digest = await crypto.subtle.digest('SHA-256', buffer)
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('')
}

export const isFileSizeValid = (bytes: number, maxFileSizeBytes: number) => {
  return bytes > 0 && bytes <= maxFileSizeBytes
}

const loadPdfJs = async () => {
  const pdfjs = await import('pdfjs-dist/legacy/build/pdf.mjs')
  if (!pdfjs.GlobalWorkerOptions.workerSrc) {
    pdfjs.GlobalWorkerOptions.workerSrc = (await import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?url')).default
  }
  return pdfjs
}

const isPasswordError = (error: unknown, code: number) => error instanceof Error && error.name === 'PasswordException' && 'code' in error && error.code === code

export const isPdfPasswordProtected = async (file: File): Promise<boolean> => {
  if (file.type !== 'application/pdf') {
    return false
  }

  const { getDocument, PasswordResponses } = await loadPdfJs()
  const loadingTask = getDocument({ data: new Uint8Array(await file.arrayBuffer()) })
  try {
    await loadingTask.promise
    return false
  } catch (error) {
    return isPasswordError(error, PasswordResponses.NEED_PASSWORD)
  } finally {
    void loadingTask.destroy()
  }
}

export const validatePdfPassword = async (file: File, password: string): Promise<boolean> => {
  const { getDocument } = await loadPdfJs()
  const loadingTask = getDocument({ data: new Uint8Array(await file.arrayBuffer()), password })
  try {
    await loadingTask.promise
    return true
  } catch {
    return false
  } finally {
    void loadingTask.destroy()
  }
}

const _MILLISECONDS_IN_A_DAY = 1000 * 60 * 60 * 24
const _RELATIVE_DATE_CUTOFF_DAYS = 7

export const formatEmailAttachmentDate = (createdAt: string): string => {
  const date = new Date(createdAt)
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  const diffDays = Math.round((today.getTime() - dateOnly.getTime()) / _MILLISECONDS_IN_A_DAY)

  if (diffDays <= 0) {
    return 'Today'
  }
  if (diffDays === 1) {
    return 'Yesterday'
  }
  if (diffDays < _RELATIVE_DATE_CUTOFF_DAYS) {
    return `${diffDays} days ago`
  }
  return formatDate(date)
}

export const truncateFilenameMiddle = (fileName: string, maxLength = 40): string => {
  if (fileName.length <= maxLength) {
    return fileName
  }

  const dotIndex = fileName.lastIndexOf('.')
  const hasExtension = dotIndex > 0 && dotIndex < fileName.length - 1
  const extension = hasExtension ? fileName.slice(dotIndex) : ''
  const base = hasExtension ? fileName.slice(0, dotIndex) : fileName

  const keep = maxLength - extension.length - 1
  if (keep <= 0) {
    return fileName
  }

  const head = Math.ceil(keep / 2)
  const tail = Math.floor(keep / 2)
  return `${base.slice(0, head)}…${tail > 0 ? base.slice(base.length - tail) : ''}${extension}`
}
